import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'antd';
import { Line } from '@antv/g2plot';
import styles from './styles/chart-placeholder.module.scss';

const ActiveUsersOverTimeChart = ({ title, data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(550);
    const [containerHeight, setContainerHeight] = useState(200);

    useEffect(() => {
        // Function to get chart div dimensions
        const updateDimensions = () => {
            if (chartRef.current) {
                // Get the actual rendered width and height of the chart div
                const rect = chartRef.current.getBoundingClientRect();
                const width = rect.width || chartRef.current.offsetWidth || 520;
                const height = rect.height || chartRef.current.offsetHeight || 200;
                if (width > 0) {
                    setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                }
                if (height > 0) {
                    setContainerHeight(Math.max(height, 150)); // Minimum height of 150px
                }
            }
        };

        // Initial dimension calculation with a small delay to ensure DOM is ready
        const timeout = setTimeout(updateDimensions, 100);

        // ResizeObserver to watch for chart div size changes
        // Use requestAnimationFrame to prevent ResizeObserver loop errors
        let rafId = null;
        const resizeObserver = new ResizeObserver((entries) => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                for (let entry of entries) {
                    // Use the actual rendered dimensions from getBoundingClientRect for accuracy
                    const rect = entry.target.getBoundingClientRect();
                    const width = rect.width || entry.contentRect.width || 520;
                    const height = rect.height || entry.contentRect.height || 200;
                    if (width > 0) {
                        setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                    }
                    if (height > 0) {
                        setContainerHeight(Math.max(height, 150)); // Minimum height of 150px
                    }
                }
                rafId = null;
            });
        });

        // Observe the chart div itself - it will resize when its container resizes
        const chartElement = chartRef.current;
        if (chartElement) {
            resizeObserver.observe(chartElement);
        }

        return () => {
            clearTimeout(timeout);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        // Use provided data or fallback to mock data
        const chartData = data || [
            { date: 'January', users: 1000 },
            { date: 'February', users: 3000 },
            { date: 'March', users: 1500 },
            { date: 'April', users: 2500 },
            { date: 'May', users: 4500 },
            { date: 'June', users: 5500 },
            { date: 'July', users: 4500 }
        ];

        if (chartRef.current && containerWidth > 0 && containerHeight > 0) {
            // Get the actual available dimensions to ensure chart fits
            const rect = chartRef.current.getBoundingClientRect();
            const actualWidth = Math.min(containerWidth, rect.width || containerWidth);
            const actualHeight = Math.min(containerHeight, rect.height || containerHeight);

            // If chart already exists, update it instead of recreating
            if (chartInstanceRef.current) {
                chartInstanceRef.current.update({
                    width: actualWidth,
                    height: actualHeight,
                    data: chartData
                });
            } else {
                // Create new chart instance
                chartInstanceRef.current = new Line(chartRef.current, {
                    data: chartData,
                    xField: 'date',
                    yField: 'users',
                    point: {
                        size: 4,
                        shape: 'circle'
                    },
                    line: {
                        color: '#1890ff',
                        size: 2
                    },
                    tooltip: {
                        formatter: (datum) => {
                            return { name: 'Active Users', value: datum.users ? datum.users.toLocaleString() : '0' };
                        }
                    },
                    yAxis: {
                        min: 0
                    },
                    height: actualHeight,
                    width: actualWidth,
                    smooth: false
                });

                chartInstanceRef.current.render();
            }
        }

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [data, containerWidth, containerHeight]);

    return (
        <Card className={styles.chartPlaceholder} title={title}>
            <div className={`${styles.chartContainer} ${styles.reducedBottomPadding}`} ref={containerRef}>
                <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '150px' }} />
            </div>
        </Card>
    );
};

export default ActiveUsersOverTimeChart;
